import { Link as LinkIcon, Pencil } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { GradeDetail, GradeResolved } from "../lib/api";

export interface GradeEntitlementFormProps {
  grade: GradeDetail;
  resolved: GradeResolved | null;
  parentGradeName?: string | null;
  onFieldChange?: (field: string, value: unknown) => void | Promise<void>;
}

type FieldSpec = {
  name: keyof GradeDetail;
  labelKey: string;
  kind: "string" | "boolean" | "number" | "decimal" | "enum";
  options?: { value: string; labelKey: string }[];
  postfixKey?: string;
};

const SECTIONS: { titleKey: string; fields: FieldSpec[] }[] = [
  {
    titleKey: "policies.grades_section_metadata",
    fields: [
      { name: "pay_group", labelKey: "policies.grades_field_pay_group", kind: "string" },
      { name: "functional_groups", labelKey: "policies.grades_field_functional_groups", kind: "string" },
      { name: "qualification_required", labelKey: "policies.grades_field_qualification", kind: "string" },
    ],
  },
  {
    titleKey: "policies.grades_section_medical",
    fields: [
      {
        name: "insurance_coverage_level",
        labelKey: "policies.grades_field_coverage_level",
        kind: "enum",
        options: [
          { value: "self", labelKey: "policies.grades_enum_self" },
          { value: "self_spouse", labelKey: "policies.grades_enum_self_spouse" },
          { value: "self_family", labelKey: "policies.grades_enum_self_family" },
          { value: "custom", labelKey: "policies.grades_enum_custom" },
        ],
      },
      { name: "members_covered_count", labelKey: "policies.grades_field_members_covered", kind: "number" },
      { name: "insurance_level_id", labelKey: "policies.grades_field_insurance_level", kind: "string" },
      { name: "insurance_category", labelKey: "policies.grades_field_insurance_category", kind: "string" },
    ],
  },
  {
    titleKey: "policies.grades_section_travel",
    fields: [
      { name: "business_travel_id", labelKey: "policies.grades_field_travel_category", kind: "string" },
      {
        name: "air_class",
        labelKey: "policies.grades_field_air_class",
        kind: "enum",
        options: [
          { value: "economy", labelKey: "policies.grades_enum_economy" },
          { value: "business", labelKey: "policies.grades_enum_business" },
          { value: "first", labelKey: "policies.grades_enum_first" },
        ],
      },
      {
        name: "hotel_rate",
        labelKey: "policies.grades_field_hotel_rate",
        kind: "enum",
        options: [
          { value: "three_star", labelKey: "policies.grades_enum_three_star" },
          { value: "four_star", labelKey: "policies.grades_enum_four_star" },
          { value: "five_star", labelKey: "policies.grades_enum_five_star" },
        ],
      },
    ],
  },
  {
    titleKey: "policies.grades_section_facilities",
    fields: [
      { name: "housing_by_company", labelKey: "policies.grades_field_housing_by_company", kind: "boolean" },
      { name: "transportation_provided", labelKey: "policies.grades_field_transport_provided", kind: "boolean" },
      { name: "schooling_amount", labelKey: "policies.grades_field_schooling_amount", kind: "decimal", postfixKey: "policies.grades_postfix_sar_year" },
      {
        name: "school_amount_eligibility",
        labelKey: "policies.grades_field_school_eligibility",
        kind: "enum",
        options: [
          { value: "all", labelKey: "policies.grades_enum_all" },
          { value: "tier1", labelKey: "policies.grades_enum_tier1" },
          { value: "none", labelKey: "policies.grades_enum_none" },
        ],
      },
      { name: "allow_exceeding_schooling", labelKey: "policies.grades_field_allow_exceed_school", kind: "boolean" },
    ],
  },
  {
    titleKey: "policies.grades_section_eos",
    fields: [
      { name: "eos_calculation_method", labelKey: "policies.grades_field_eos_method", kind: "string" },
    ],
  },
  {
    titleKey: "policies.grades_section_performance",
    fields: [
      { name: "competencies_percentage", labelKey: "policies.grades_field_competencies_pct", kind: "number", postfixKey: "policies.grades_postfix_pct" },
      { name: "objectives_percentage", labelKey: "policies.grades_field_objectives_pct", kind: "number", postfixKey: "policies.grades_postfix_pct" },
    ],
  },
  {
    titleKey: "policies.grades_section_leave_tickets",
    fields: [
      { name: "annual_leave_days_default", labelKey: "policies.grades_field_annual_leave_days", kind: "number", postfixKey: "policies.grades_postfix_days" },
      { name: "ticket_count_per_year", labelKey: "policies.grades_field_tickets_per_year", kind: "number" },
      {
        name: "ticket_class",
        labelKey: "policies.grades_field_ticket_class",
        kind: "enum",
        options: [
          { value: "economy", labelKey: "policies.grades_enum_economy" },
          { value: "business", labelKey: "policies.grades_enum_business" },
          { value: "first", labelKey: "policies.grades_enum_first" },
        ],
      },
      {
        name: "ticket_entitlement_period",
        labelKey: "policies.grades_field_ticket_period",
        kind: "enum",
        options: [
          { value: "yearly", labelKey: "policies.grades_enum_yearly" },
          { value: "bi_yearly", labelKey: "policies.grades_enum_bi_yearly" },
        ],
      },
    ],
  },
];

function FieldRow({
  grade,
  resolved,
  spec,
  parentGradeName,
  onFieldChange,
}: {
  grade: GradeDetail;
  resolved: GradeResolved | null;
  spec: FieldSpec;
  parentGradeName?: string | null;
  onFieldChange?: (field: string, value: unknown) => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const ownValue = grade[spec.name];
  const resolvedField = resolved?.fields[spec.name as string];
  const inherited =
    resolvedField !== undefined &&
    resolvedField.source_grade_id !== null &&
    resolvedField.source_grade_id !== grade.id;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(ownValue === null ? "" : String(ownValue));

  const editable = !!onFieldChange;

  const save = () => {
    if (!onFieldChange) return;
    let parsed: unknown = draft === "" ? null : draft;
    if (spec.kind === "number" || spec.kind === "decimal") {
      parsed = draft === "" ? null : Number(draft);
    } else if (spec.kind === "boolean") {
      parsed = draft === "true";
    }
    onFieldChange(spec.name as string, parsed);
    setEditing(false);
  };

  const displayValue = (() => {
    if (ownValue !== null && ownValue !== undefined && ownValue !== "") {
      // For enum, translate the value
      if (spec.kind === "enum" && spec.options) {
        const opt = spec.options.find((o) => o.value === String(ownValue));
        if (opt) return t(opt.labelKey);
      }
      return String(ownValue);
    }
    if (inherited && resolvedField?.value !== null && resolvedField?.value !== undefined) {
      if (spec.kind === "enum" && spec.options) {
        const opt = spec.options.find((o) => o.value === String(resolvedField.value));
        if (opt) return t(opt.labelKey);
      }
      return String(resolvedField.value);
    }
    return null;
  })();

  return (
    <div className="flex flex-col gap-1 py-2">
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
          {t(spec.labelKey)}
        </span>
        {inherited && (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-700"
            title={t("policies.grades_inherited_tooltip", { name: parentGradeName ?? t("policies.grades_parent_default") })}
          >
            <LinkIcon className="h-3 w-3" />
            {t("policies.grades_inherited_label")}
          </span>
        )}
      </div>

      {editing && editable ? (
        <div className="flex items-center gap-1">
          {spec.kind === "boolean" ? (
            <select
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={save}
              className="rounded border border-slate-300 px-2 py-1 text-sm"
            >
              <option value="">—</option>
              <option value="true">{t("common.yes")}</option>
              <option value="false">{t("common.no")}</option>
            </select>
          ) : spec.kind === "enum" ? (
            <select
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={save}
              className="rounded border border-slate-300 px-2 py-1 text-sm"
            >
              <option value="">—</option>
              {(spec.options ?? []).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </option>
              ))}
            </select>
          ) : (
            <input
              autoFocus
              type={spec.kind === "number" || spec.kind === "decimal" ? "number" : "text"}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") save();
                if (e.key === "Escape") setEditing(false);
              }}
              onBlur={save}
              className="rounded border border-slate-300 px-2 py-1 text-sm"
            />
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => {
            if (!editable) return;
            setDraft(ownValue === null ? "" : String(ownValue));
            setEditing(true);
          }}
          className={`group flex items-center gap-1 text-start text-sm ${
            editable ? "hover:text-slate-700" : "cursor-default"
          } ${displayValue === null ? "italic text-slate-400" : "text-slate-900"}`}
        >
          <span className="font-mono tabular-nums">
            {displayValue ?? "—"}
            {displayValue !== null && spec.postfixKey && (
              <span className="ms-1 text-xs text-slate-500">{t(spec.postfixKey)}</span>
            )}
          </span>
          {editable && (
            <Pencil className="h-3 w-3 opacity-0 transition group-hover:opacity-100" />
          )}
        </button>
      )}
    </div>
  );
}

export function GradeEntitlementForm(props: GradeEntitlementFormProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      {SECTIONS.map(({ titleKey, fields }) => (
        <div
          key={titleKey}
          className="rounded-xl border border-slate-200 bg-white p-4"
        >
          <h3 className="mb-2 text-sm font-bold text-slate-700">{t(titleKey)}</h3>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 divide-y divide-slate-100">
            {fields.map((spec) => (
              <FieldRow
                key={spec.name as string}
                grade={props.grade}
                resolved={props.resolved}
                spec={spec}
                parentGradeName={props.parentGradeName}
                onFieldChange={props.onFieldChange}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
